import { useEffect, useRef, useState } from "react";

import type { GroupRegistryController } from "../groups/GroupRegistry.context";
import { reportGroupRegistryError } from "../groups/groupRegistry.repository";
import type { WorkspaceStoreApi } from "../workspace/Workspace.context";
import type {
  GroupColorSlot,
  GroupId,
  SymbolGroup,
} from "../workspace/workspace.types";
import {
  getEffectiveWidgetSymbol,
  getGroupLetter,
} from "../workspace/workspace.utils";
import {
  deriveGroupSelect,
  type GroupSelectDerived,
  getGroupDisplayName,
} from "./GroupSelect.derive";

export type GroupSelectRowKey = GroupId | "none";

export interface GroupSelectRowView {
  group: SymbolGroup;
  letter: string;
  displayName: string;
  draftName: string;
  isActive: boolean;
  canDelete: boolean;
  memberCount: number;
  linkedWidgetsLabel?: string;
  isEditing: boolean;
  showMembership: boolean;
  isSelectingForDelete: boolean;
  isSelected: boolean;
  isHovered: boolean;
}

export interface GroupSelectBulkDeleteControllerOutput {
  isSelectingForDelete: boolean;
  selectedGroupIds: GroupId[];
  allGroupsSelected: boolean;
  pendingBulkDelete: boolean;
  onStartDeleteGroups: () => void;
  onCancelDeleteGroups: () => void;
  onToggleGroupSelected: (groupId: GroupId) => void;
  onToggleSelectAll: () => void;
  onRequestBulkDelete: () => void;
  onCancelBulkDeleteConfirm: () => void;
  onConfirmBulkDelete: () => void;
}

export interface UseGroupSelectViewControllerInput {
  derived: GroupSelectDerived;
  noneLabel?: string;
  unassignedAriaLabel?: string;
}

export interface GroupSelectControllerReturn {
  activeGroup: SymbolGroup | null;
  rows: GroupSelectRowView[];
  canCreate: boolean;
  colorOptions: readonly GroupColorSlot[];
  noneLabel?: string;
  unassignedAriaLabel?: string;
  open: boolean;
  noneHovered: boolean;
  pendingDeleteId: GroupId | null;
  pendingDeleteCount: number;
  isSelectingForDelete: boolean;
  selectedGroupIds: GroupId[];
  allGroupsSelected: boolean;
  pendingBulkDelete: boolean;
  pendingBulkDeleteCount: number;
  onOpenChange: (open: boolean) => void;
  onNoneHoverChange: (hovered: boolean) => void;
  onSelectNone: () => void;
  onSelectGroup: (groupId: GroupId) => void;
  onCreateGroup: () => void;
  onStartDeleteGroups: () => void;
  onCancelDeleteGroups: () => void;
  onToggleGroupSelected: (groupId: GroupId) => void;
  onToggleSelectAll: () => void;
  onRequestBulkDelete: () => void;
  onCancelBulkDeleteConfirm: () => void;
  onConfirmBulkDelete: () => void;
  onGroupHoverChange: (groupId: GroupId, hovered: boolean) => void;
  onStartNameEdit: (groupId: GroupId) => void;
  onDraftNameChange: (groupId: GroupId, value: string) => void;
  onCommitName: (groupId: GroupId) => void;
  onCancelNameEdit: (groupId: GroupId) => void;
  onChangeGroupColor: (groupId: GroupId, colorSlot: GroupColorSlot) => void;
  onRequestDelete: (groupId: GroupId) => void;
  onSingleDeleteOpenChange: (open: boolean) => void;
  onConfirmSingleDelete: () => void;
}

export interface UseGroupSelectControllerInput {
  widgetId: string;
  groups: SymbolGroup[];
  activeGroupId: GroupId | null;
  memberCounts: Partial<Record<GroupId, number>>;
  linkedWidgetsByGroup: Partial<Record<GroupId, string>>;
  store: WorkspaceStoreApi;
  registry: GroupRegistryController | null;
}

type GroupNameDraft = {
  sourceDisplayName: string;
  value: string;
};
type GroupNameDrafts = Partial<Record<GroupId, GroupNameDraft>>;

export const useGroupSelectBulkDeleteController = (
  groups: SymbolGroup[],
  deleteGroup: (groupId: GroupId) => void,
): GroupSelectBulkDeleteControllerOutput => {
  const [isSelectingForDelete, setIsSelectingForDelete] = useState(false);
  const [selectedIds, setSelectedIds] = useState<GroupId[]>([]);
  const [pendingBulkDelete, setPendingBulkDelete] = useState(false);

  const liveIds = new Set(groups.map((group) => group.id));
  const selectedGroupIds = selectedIds.filter((id) => liveIds.has(id));
  const allGroupsSelected =
    groups.length > 0 && selectedGroupIds.length === groups.length;

  const exitDeleteFlow = () => {
    setIsSelectingForDelete(false);
    setSelectedIds([]);
    setPendingBulkDelete(false);
  };

  return {
    isSelectingForDelete,
    selectedGroupIds,
    allGroupsSelected,
    pendingBulkDelete,
    onStartDeleteGroups: () => {
      setIsSelectingForDelete(true);
      setSelectedIds([]);
      setPendingBulkDelete(false);
    },
    onCancelDeleteGroups: exitDeleteFlow,
    onToggleGroupSelected: (groupId) => {
      setSelectedIds((current) =>
        current.includes(groupId)
          ? current.filter((id) => id !== groupId)
          : [...current, groupId],
      );
    },
    onToggleSelectAll: () => {
      setSelectedIds(allGroupsSelected ? [] : groups.map((group) => group.id));
    },
    onRequestBulkDelete: () => {
      if (selectedGroupIds.length === 0) {
        return;
      }
      setPendingBulkDelete(true);
    },
    onCancelBulkDeleteConfirm: () => {
      setPendingBulkDelete(false);
    },
    onConfirmBulkDelete: () => {
      for (const groupId of selectedGroupIds) {
        deleteGroup(groupId);
      }
      exitDeleteFlow();
    },
  };
};

const currentDisplayNameFor = (
  groups: SymbolGroup[],
  groupId: GroupId,
): string => {
  const group = groups.find((candidate) => candidate.id === groupId);
  return group ? getGroupDisplayName(group) : "";
};

export const useGroupSelectViewController = ({
  derived,
  noneLabel,
  unassignedAriaLabel,
}: UseGroupSelectViewControllerInput): GroupSelectControllerReturn => {
  const [open, setOpen] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState<GroupId | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<GroupId | null>(null);
  const [hoverByKey, setHoverByKey] = useState<
    Partial<Record<GroupSelectRowKey, boolean>>
  >({});
  const [drafts, setDrafts] = useState<GroupNameDrafts>({});

  const bulk = useGroupSelectBulkDeleteController(
    derived.groups,
    derived.onDeleteGroup,
  );

  const pendingBulkDeleteRef = useRef(bulk.pendingBulkDelete);
  pendingBulkDeleteRef.current = bulk.pendingBulkDelete;

  useEffect(() => {
    const liveIds = new Set(derived.groups.map((group) => group.id));
    const displayById = new Map(
      derived.groups.map((group) => [group.id, getGroupDisplayName(group)]),
    );

    setDrafts((current) => {
      let changed = false;
      const next: GroupNameDrafts = { ...current };
      for (const id of Object.keys(next) as GroupId[]) {
        const displayName = displayById.get(id);
        const entry = next[id];
        if (
          !liveIds.has(id) ||
          displayName === undefined ||
          entry?.sourceDisplayName !== displayName
        ) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : current;
    });

    setHoverByKey((current) => {
      let changed = false;
      const next: Partial<Record<GroupSelectRowKey, boolean>> = { ...current };
      for (const key of Object.keys(next) as GroupSelectRowKey[]) {
        if (key !== "none" && !liveIds.has(key)) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [derived.groups]);

  const clearRowEphemera = () => {
    setDrafts({});
    setHoverByKey({});
  };

  const rows: GroupSelectRowView[] = derived.groups.map((group) => {
    const letter = getGroupLetter(group.id);
    const displayName = getGroupDisplayName(group);
    const entry = drafts[group.id];
    const draftName =
      entry?.sourceDisplayName === displayName ? entry.value : displayName;
    const linkedWidgetsLabel = derived.linkedWidgetsByGroup[group.id];
    const isEditing = editingGroupId === group.id;
    return {
      group,
      letter,
      displayName,
      draftName,
      isActive: derived.activeGroup?.id === group.id,
      canDelete: derived.canDelete,
      memberCount: derived.memberCounts[group.id] ?? 0,
      linkedWidgetsLabel,
      isEditing,
      showMembership: !isEditing && !!linkedWidgetsLabel,
      isSelectingForDelete: bulk.isSelectingForDelete,
      isSelected: bulk.selectedGroupIds.includes(group.id),
      isHovered: hoverByKey[group.id] === true,
    };
  });

  const pendingDeleteCount =
    pendingDeleteId !== null ? (derived.memberCounts[pendingDeleteId] ?? 0) : 0;
  const pendingBulkDeleteCount = bulk.selectedGroupIds.reduce(
    (sum, groupId) => sum + (derived.memberCounts[groupId] ?? 0),
    0,
  );

  return {
    activeGroup: derived.activeGroup,
    rows,
    canCreate: derived.canCreate,
    colorOptions: derived.colorOptions,
    noneLabel,
    unassignedAriaLabel,
    open,
    noneHovered: hoverByKey.none === true,
    pendingDeleteId,
    pendingDeleteCount,
    isSelectingForDelete: bulk.isSelectingForDelete,
    selectedGroupIds: bulk.selectedGroupIds,
    allGroupsSelected: bulk.allGroupsSelected,
    pendingBulkDelete: bulk.pendingBulkDelete,
    pendingBulkDeleteCount,
    onOpenChange: (nextOpen) => {
      if (!nextOpen && pendingBulkDeleteRef.current) {
        return;
      }
      setOpen(nextOpen);
      if (!nextOpen) {
        clearRowEphemera();
        setEditingGroupId(null);
        bulk.onCancelDeleteGroups();
      }
    },
    onNoneHoverChange: (hovered) => {
      setHoverByKey((current) => ({ ...current, none: hovered }));
    },
    onSelectNone: () => {
      derived.onSelectNone();
      setOpen(false);
      clearRowEphemera();
    },
    onSelectGroup: (groupId) => {
      derived.onSelectGroup(groupId);
      setOpen(false);
      clearRowEphemera();
    },
    onCreateGroup: derived.onCreateGroup,
    onStartDeleteGroups: bulk.onStartDeleteGroups,
    onCancelDeleteGroups: bulk.onCancelDeleteGroups,
    onToggleGroupSelected: bulk.onToggleGroupSelected,
    onToggleSelectAll: bulk.onToggleSelectAll,
    onRequestBulkDelete: bulk.onRequestBulkDelete,
    onCancelBulkDeleteConfirm: bulk.onCancelBulkDeleteConfirm,
    onConfirmBulkDelete: bulk.onConfirmBulkDelete,
    onGroupHoverChange: (groupId, hovered) => {
      setHoverByKey((current) => ({ ...current, [groupId]: hovered }));
    },
    onStartNameEdit: (groupId) => {
      setEditingGroupId(groupId);
    },
    onDraftNameChange: (groupId, value) => {
      const sourceDisplayName = currentDisplayNameFor(derived.groups, groupId);
      setDrafts((current) => ({
        ...current,
        [groupId]: { sourceDisplayName, value },
      }));
    },
    onCommitName: (groupId) => {
      setEditingGroupId(null);
      const displayName = currentDisplayNameFor(derived.groups, groupId);
      const entry = drafts[groupId];
      const draftName =
        entry?.sourceDisplayName === displayName ? entry.value : displayName;
      if (draftName.trim() !== displayName) {
        derived.onRenameGroup(groupId, draftName);
      }
    },
    onCancelNameEdit: (groupId) => {
      const displayName = currentDisplayNameFor(derived.groups, groupId);
      setDrafts((current) => ({
        ...current,
        [groupId]: { sourceDisplayName: displayName, value: displayName },
      }));
      setEditingGroupId(null);
    },
    onChangeGroupColor: derived.onChangeGroupColor,
    onRequestDelete: setPendingDeleteId,
    onSingleDeleteOpenChange: (dialogOpen) => {
      if (!dialogOpen) {
        setPendingDeleteId(null);
      }
    },
    onConfirmSingleDelete: () => {
      if (pendingDeleteId !== null) {
        derived.onDeleteGroup(pendingDeleteId);
      }
    },
  };
};

export const useGroupSelectController = ({
  widgetId,
  groups,
  activeGroupId,
  memberCounts,
  linkedWidgetsByGroup,
  store,
  registry,
}: UseGroupSelectControllerInput): GroupSelectControllerReturn => {
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const {
    assignWidgetGroup,
    createAndAssignGroup,
    deleteGroup,
    setGroupName,
    setGroupColor,
  } = store.getState();

  const persistColor = (groupId: GroupId, colorSlot: GroupColorSlot) => {
    if (registry) {
      void registry
        .setGroupColor(groupId, colorSlot)
        .catch(reportGroupRegistryError);
      return;
    }
    setGroupColor(groupId, colorSlot);
  };

  const persistName = (groupId: GroupId, name: string) => {
    if (registry) {
      void registry.setGroupName(groupId, name).catch(reportGroupRegistryError);
      return;
    }
    setGroupName(groupId, name);
  };

  const persistDelete = (groupId: GroupId) => {
    if (registry) {
      void registry.deleteGroup(groupId).catch(reportGroupRegistryError);
      return;
    }
    deleteGroup(groupId);
  };

  const persistCreate = () => {
    if (!registry) {
      createAndAssignGroup(widgetId);
      return;
    }
    const seedSymbol = getEffectiveWidgetSymbol(store.getState(), widgetId);
    void registry
      .createGroup({ seedSymbol })
      .then((id) => {
        if (id === undefined || !mountedRef.current) {
          return;
        }
        if (!store.getState().widgets[widgetId]) {
          return;
        }
        store.getState().assignWidgetGroup(widgetId, id);
      })
      .catch(reportGroupRegistryError);
  };

  const derived = deriveGroupSelect({
    groups,
    activeGroupId,
    memberCounts,
    linkedWidgetsByGroup,
    selectNone: () => {
      assignWidgetGroup(widgetId, undefined);
    },
    selectGroup: (groupId) => {
      assignWidgetGroup(widgetId, groupId);
    },
    createGroup: persistCreate,
    deleteGroup: persistDelete,
    setGroupName: persistName,
    setGroupColor: persistColor,
  });

  return useGroupSelectViewController({ derived });
};
